<template>
  <div
    v-if="visible"
    ref="banner"
    class="protego-billing-payment-warning"
    :class="{ locked: accessLocked }"
    role="alert"
  >
    <div class="protego-billing-payment-warning__inner">
      <span>{{ message }}</span>
      <div class="protego-billing-payment-warning__actions">
        <button
          type="button"
          class="btn btn-sm mb-0"
          :class="accessLocked ? 'btn-danger' : 'btn-warning'"
          :disabled="opening"
          @click="updatePayment"
        >
          {{ opening ? "Opening…" : "Update payment" }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from "vuex";

const BODY_CLASS = "protego-billing-banner-visible";
const HEIGHT_VAR = "--protego-billing-banner-height";

export default {
  name: "PlatformBillingPaymentBanner",
  data() {
    return {
      opening: false,
      resizeObserver: null,
    };
  },
  computed: {
    ...mapState("agencyBilling", ["accessLocked", "paymentWarning"]),
    ...mapGetters("agencyBilling", ["showPaymentWarning", "showPaymentLock", "paymentBannerMessage"]),
    visible() {
      return this.showPaymentWarning || this.showPaymentLock;
    },
    message() {
      return this.paymentBannerMessage;
    },
  },
  watch: {
    visible: {
      immediate: true,
      handler(isVisible) {
        this.$nextTick(() => {
          if (isVisible) {
            this.applyBannerLayout();
          } else {
            this.clearBannerLayout();
          }
        });
      },
    },
  },
  mounted() {
    if (typeof ResizeObserver !== "undefined") {
      this.resizeObserver = new ResizeObserver(() => {
        if (this.visible) {
          this.applyBannerLayout();
        }
      });
    }
    this.$nextTick(() => {
      if (this.visible) {
        this.applyBannerLayout();
        if (this.resizeObserver && this.$refs.banner) {
          this.resizeObserver.observe(this.$refs.banner);
        }
      }
    });
  },
  updated() {
    if (this.visible && this.resizeObserver && this.$refs.banner) {
      this.resizeObserver.observe(this.$refs.banner);
    }
  },
  beforeUnmount() {
    this.clearBannerLayout();
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  },
  methods: {
    applyBannerLayout() {
      const el = this.$refs.banner;
      if (!el || typeof document === "undefined") {
        return;
      }
      const height = Math.ceil(el.getBoundingClientRect().height);
      document.documentElement.style.setProperty(HEIGHT_VAR, `${height}px`);
      document.body.classList.add(BODY_CLASS);
      if (this.resizeObserver) {
        this.resizeObserver.observe(el);
      }
    },
    clearBannerLayout() {
      if (typeof document === "undefined") {
        return;
      }
      document.body.classList.remove(BODY_CLASS);
      document.documentElement.style.removeProperty(HEIGHT_VAR);
    },
    async updatePayment() {
      if (this.$route.path !== "/agency/billing") {
        await this.$router.push({ path: "/agency/billing" }).catch(() => undefined);
      }
      this.opening = true;
      try {
        await this.$store.dispatch("agencyBilling/openStripePortal");
      } catch (error) {
        console.error("openStripePortal failed", error);
        this.opening = false;
      }
    },
  },
};
</script>

<style scoped>
/* Full viewport width strip above the floating sidenav */
.protego-billing-payment-warning {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  width: 100%;
  box-sizing: border-box;
  background: #fff3cd;
  border-bottom: 1px solid #ffc107;
  color: #856404;
  padding: 10px 16px;
  font-size: 0.875rem;
  line-height: 1.4;
  z-index: 1200;
}

.protego-billing-payment-warning.locked {
  background: #f8d7da;
  border-bottom-color: #f5c2c7;
  color: #842029;
}

.protego-billing-payment-warning__inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  margin: 0;
}

.protego-billing-payment-warning__actions {
  flex-shrink: 0;
}

.protego-billing-payment-warning__actions .btn {
  white-space: nowrap;
}
</style>

<style>
/* Push fixed sidenav + main content below the full-bleed banner */
body.protego-billing-banner-visible #sidenav-main.sidenav {
  top: var(--protego-billing-banner-height, 0px) !important;
  height: calc(100% - var(--protego-billing-banner-height, 0px)) !important;
  max-height: calc(100% - var(--protego-billing-banner-height, 0px)) !important;
}

body.protego-billing-banner-visible .main-content {
  padding-top: var(--protego-billing-banner-height, 0px);
}
</style>
